import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface BlogPost {
  id: number;
  title: string;
  category: string;
  excerpt: string;
  author: string;
  date: string;
  readTime: string;
  image: string;
}

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const categories = ['All', 'Mental Health', 'Treatment Options', 'Patient Resources', 'Clinic News', 'Family Support'];

  const blogPosts: BlogPost[] = [
    {
      id: 1,
      title: 'Understanding Anxiety Disorders: Symptoms, Types, and Treatment Approaches',
      category: 'Mental Health',
      excerpt: 'Anxiety disorders affect millions of Americans. Learn about the different types of anxiety disorders, common symptoms, and evidence-based treatment options including therapy and medication management.',
      author: 'Dr. Sarah Mitchell',
      date: 'January 15, 2025',
      readTime: '8 min read',
      image: 'https://public.readdy.ai/ai/img_res/1f0850ddbaf7f4464dc698beee6df3e6.jpg'
    },
    {
      id: 2,
      title: 'Medication Management: What to Expect During Your First Psychiatric Appointment',
      category: 'Treatment Options',
      excerpt: 'Starting psychiatric medication can feel overwhelming. This guide walks you through what happens during your initial evaluation, how medications are selected, and what to expect in the first few weeks of treatment.',
      author: 'Dr. James Rodriguez',
      date: 'January 10, 2025',
      readTime: '6 min read',
      image: 'https://readdy.ai/api/search-image?query=A%20professional%20and%20reassuring%20medical%20consultation%20scene%20showing%20a%20compassionate%20healthcare%20provider%20discussing%20treatment%20options%20with%20a%20patient%20in%20a%20modern%20clinical%20setting%20with%20medical%20charts%20and%20a%20calm%20atmosphere%20emphasizing%20patient-centered%20care&width=800&height=500&seq=blog2&orientation=landscape'
    },
    {
      id: 3,
      title: 'Telepsychiatry: Accessing Quality Mental Healthcare from Home',
      category: 'Patient Resources',
      excerpt: 'Virtual mental health appointments offer convenience and accessibility. Discover how telepsychiatry works, its benefits, and tips for making the most of your video appointments with our providers.',
      author: 'Emily Chen, LCSW',
      date: 'January 5, 2025',
      readTime: '5 min read',
      image: 'https://readdy.ai/api/search-image?query=A%20comfortable%20home%20office%20setup%20showing%20a%20person%20having%20a%20positive%20video%20call%20consultation%20with%20their%20mental%20health%20provider%20on%20a%20laptop%2C%20with%20soft%20lighting%2C%20plants%2C%20and%20a%20peaceful%20home%20environment%20that%20demonstrates%20accessible%20telehealth%20services&width=800&height=500&seq=blog3&orientation=landscape'
    },
    {
      id: 4,
      title: 'Supporting a Loved One with Depression: A Family Guide',
      category: 'Family Support',
      excerpt: 'When someone you care about is struggling with depression, knowing how to help can be challenging. Learn practical ways to provide support, recognize warning signs, and encourage treatment.',
      author: 'Dr. Michael Thompson',
      date: 'December 28, 2024',
      readTime: '7 min read',
      image: 'https://readdy.ai/api/search-image?query=A%20warm%20and%20supportive%20scene%20of%20two%20people%20sitting%20together%20in%20a%20comfortable%20living%20room%20having%20a%20caring%20conversation%2C%20with%20soft%20natural%20light%20and%20comforting%20home%20decor%20that%20conveys%20emotional%20support%20and%20family%20connection%20during%20mental%20health%20challenges&width=800&height=500&seq=blog4&orientation=landscape'
    },
    {
      id: 5,
      title: 'Dual Diagnosis Treatment: Addressing Mental Health and Substance Use Together',
      category: 'Treatment Options',
      excerpt: 'Co-occurring disorders require integrated treatment. Explore how our dual diagnosis program addresses both mental health conditions and substance use disorders simultaneously for better outcomes.',
      author: 'Dr. Lisa Anderson',
      date: 'December 20, 2024',
      readTime: '9 min read',
      image: 'https://readdy.ai/api/search-image?query=A%20hopeful%20and%20professional%20treatment%20center%20environment%20with%20natural%20light%2C%20showing%20a%20peaceful%20counseling%20space%20with%20comfortable%20seating%20and%20calming%20colors%20that%20represents%20integrated%20care%20for%20mental%20health%20and%20substance%20use%20recovery&width=800&height=500&seq=blog5&orientation=landscape'
    },
    {
      id: 6,
      title: 'Sleep and Mental Health: Understanding the Critical Connection',
      category: 'Mental Health',
      excerpt: 'Poor sleep and mental health conditions often go hand in hand. Discover how sleep affects your mental wellbeing, common sleep disorders in psychiatric patients, and strategies to improve sleep quality.',
      author: 'Dr. Rachel Green',
      date: 'December 15, 2024',
      readTime: '7 min read',
      image: 'https://readdy.ai/api/search-image?query=A%20peaceful%20bedroom%20scene%20with%20soft%20moonlight%20filtering%20through%20curtains%2C%20comfortable%20bedding%20in%20calming%20blue%20and%20white%20tones%2C%20and%20serene%20decor%20that%20promotes%20restful%20sleep%20and%20mental%20wellness%20with%20plants%20and%20minimal%20design%20elements&width=800&height=500&seq=blog6new&orientation=landscape'
    },
    {
      id: 7,
      title: 'Managing PTSD: Evidence-Based Treatments That Work',
      category: 'Mental Health',
      excerpt: 'Post-traumatic stress disorder can significantly impact daily life. Discover the latest evidence-based treatments including trauma-focused therapy and medication options that help patients heal and recover.',
      author: 'Dr. Rachel Green',
      date: 'December 8, 2024',
      readTime: '10 min read',
      image: 'https://readdy.ai/api/search-image?query=A%20peaceful%20and%20safe%20therapeutic%20environment%20with%20soft%20lighting%20and%20calming%20natural%20elements%20like%20plants%20and%20water%20features%2C%20creating%20a%20secure%20space%20for%20trauma%20recovery%20and%20PTSD%20treatment%20with%20warm%20neutral%20tones%20and%20comfortable%20seating&width=800&height=500&seq=blog7&orientation=landscape'
    },
    {
      id: 8,
      title: 'Geriatric Mental Health: Addressing Depression and Anxiety in Older Adults',
      category: 'Mental Health',
      excerpt: 'Mental health concerns in seniors are often overlooked. Learn about the unique challenges older adults face, how to recognize symptoms, and specialized treatment approaches for geriatric patients.',
      author: 'Dr. William Foster',
      date: 'November 30, 2024',
      readTime: '7 min read',
      image: 'https://readdy.ai/api/search-image?query=A%20warm%20and%20dignified%20scene%20of%20an%20elderly%20person%20in%20a%20comfortable%20senior-friendly%20environment%20with%20natural%20light%2C%20plants%2C%20and%20accessible%20design%20elements%20that%20convey%20respect%20and%20specialized%20care%20for%20geriatric%20mental%20health%20services&width=800&height=500&seq=blog8&orientation=landscape'
    },
    {
      id: 9,
      title: 'Your Rights as a Mental Health Patient: Understanding Informed Consent and Privacy',
      category: 'Patient Resources',
      excerpt: 'Knowing your rights is essential to receiving quality care. This article explains informed consent, HIPAA protections, and what you can expect regarding confidentiality in psychiatric treatment.',
      author: 'Jennifer Martinez, Patient Advocate',
      date: 'November 22, 2024',
      readTime: '6 min read',
      image: 'https://readdy.ai/api/search-image?query=A%20professional%20and%20trustworthy%20medical%20office%20setting%20with%20privacy%20screens%2C%20secure%20filing%20systems%2C%20and%20a%20clean%20modern%20design%20that%20emphasizes%20patient%20confidentiality%2C%20HIPAA%20compliance%2C%20and%20professional%20healthcare%20standards%20with%20neutral%20calming%20colors&width=800&height=500&seq=blog9&orientation=landscape'
    },
    {
      id: 10,
      title: 'Bipolar Disorder: Recognizing Mood Episodes and Maintaining Stability',
      category: 'Mental Health',
      excerpt: 'Living with bipolar disorder requires understanding your condition and maintaining consistent treatment. Learn to identify mood episodes, work with your provider, and develop strategies for long-term stability.',
      author: 'Dr. Sarah Mitchell',
      date: 'November 15, 2024',
      readTime: '8 min read',
      image: 'https://readdy.ai/api/search-image?query=A%20balanced%20and%20harmonious%20visual%20representation%20showing%20contrasting%20elements%20in%20peaceful%20coexistence%20with%20soft%20gradients%2C%20natural%20elements%2C%20and%20calming%20colors%20that%20symbolize%20mood%20stability%20and%20mental%20health%20balance%20in%20a%20serene%20therapeutic%20environment&width=800&height=500&seq=blog10&orientation=landscape'
    },
    {
      id: 11,
      title: 'Preparing for Your First Psychiatric Evaluation: What to Bring and What to Expect',
      category: 'Patient Resources',
      excerpt: 'Your first psychiatric appointment is an important step. This comprehensive guide helps you prepare by explaining what information to bring, questions you will be asked, and how the evaluation process works.',
      author: 'Dr. James Rodriguez',
      date: 'November 8, 2024',
      readTime: '5 min read',
      image: 'https://readdy.ai/api/search-image?query=A%20welcoming%20and%20organized%20medical%20office%20reception%20area%20with%20comfortable%20waiting%20room%20seating%2C%20natural%20light%2C%20plants%2C%20and%20a%20professional%20yet%20warm%20atmosphere%20that%20helps%20patients%20feel%20prepared%20and%20comfortable%20for%20their%20first%20psychiatric%20appointment&width=800&height=500&seq=blog11&orientation=landscape'
    },
    {
      id: 12,
      title: 'Mindfulness and Meditation for Mental Health: Practical Techniques for Daily Life',
      category: 'Patient Resources',
      excerpt: 'Mindfulness practices can complement traditional psychiatric treatment. Learn evidence-based meditation techniques, breathing exercises, and practical strategies to reduce stress and improve emotional regulation.',
      author: 'Emily Chen, LCSW',
      date: 'November 1, 2024',
      readTime: '6 min read',
      image: 'https://readdy.ai/api/search-image?query=A%20tranquil%20meditation%20space%20with%20a%20person%20sitting%20peacefully%20in%20lotus%20position%20surrounded%20by%20soft%20natural%20light%2C%20indoor%20plants%2C%20candles%2C%20and%20minimalist%20zen%20decor%20in%20calming%20earth%20tones%20that%20promotes%20mindfulness%20and%20mental%20wellness%20practices&width=800&height=500&seq=blog12new&orientation=landscape'
    }
  ];

  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const getPostUrl = (id: number) => {
    const urlMap: Record<number, string> = {
      1: 'https://www.nimh.nih.gov/health/topics/anxiety-disorders',
      2: 'https://www.psychiatry.org/patients-families/what-to-expect-from-your-first-appointment',
      3: 'https://www.apa.org/topics/telehealth/telepsychology',
      4: 'https://www.nami.org/Your-Journey/Family-Members-and-Caregivers/Supporting-Someone-with-Depression',
      5: 'https://www.samhsa.gov/find-help/disorders/co-occurring-disorders',
      6: 'https://www.sleepfoundation.org/mental-health',
      7: 'https://www.ptsd.va.gov/understand_tx/tx_basics.asp',
      8: 'https://www.nia.nih.gov/health/mental-and-emotional-health',
      9: 'https://www.hhs.gov/hipaa/for-individuals/guidance-materials-for-consumers/index.html',
      10: 'https://www.nimh.nih.gov/health/topics/bipolar-disorder',
      11: 'https://www.nami.org/About-Mental-Illness/Treatments/Getting-Treatment-During-a-Crisis',
      12: 'https://www.nccih.nih.gov/health/meditation-and-mindfulness-what-you-need-to-know'
    };
    return urlMap[id] || '/blog';
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner */}
      <section className="relative h-[40vh] flex items-center justify-center bg-gradient-to-r from-primary to-primary-dark">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLXdpZHRoPSIyIi8+PC9nPjwvc3ZnPg==')]"></div>
        </div>
        <div className="relative z-10 text-center px-6">
          <motion.h1
            className="text-5xl md:text-6xl font-bold text-white mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Mental Health Resources & Insights
          </motion.h1>
          <motion.p
            className="text-white/80 text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/" className="hover:text-white cursor-pointer">Home</Link> <span className="mx-2">/</span> Blog
          </motion.p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-[rgba(75,162,75)] text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Blog Posts Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <a
                key={post.id}
                href={getPostUrl(post.id)}
                target={post.id === 6 || post.id === 12 ? '_self' : '_blank'}
                rel={post.id === 6 || post.id === 12 ? '' : 'noopener noreferrer'}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all overflow-hidden border border-gray-100 group cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[rgba(75,162,75)] text-white px-4 py-1 rounded-full text-sm font-semibold">
                      {post.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[rgba(75,162,75)] transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {post.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {post.readTime}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{post.date}</span>
                    <span className="text-[rgba(75,162,75)] font-semibold group-hover:text-[rgba(75,162,75,0.8)] flex items-center gap-1">
                      Read More
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-16">
              <i className="ri-file-search-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-lg">No articles found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="container mx-auto px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-[rgba(75,162,75)] to-[rgba(75,162,75,0.9)] rounded-xl p-10 text-white text-center">
            <i className="ri-mail-line text-5xl mb-4"></i>
            <h3 className="text-3xl font-bold mb-4">Stay Informed</h3>
            <p className="text-white/80 mb-8 text-lg max-w-2xl mx-auto">
              Subscribe to our newsletter for the latest mental health insights, clinic updates, and patient resources delivered to your inbox.
            </p>
            <div className="max-w-md mx-auto flex gap-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
              />
              <button className="bg-white text-[rgba(75,162,75)] px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
